import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Table, Alert, Input } from "reactstrap";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import { useGetReportTable } from "api/reports/service";
import { useGetChannels } from "api/channels/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { useReportScheduleTheme, getReportSchedulePanelBoxStyle } from "./reportScheduleTheme";
import {
  getDynamicDateOption,
  normalizeFilterValueForOperator,
  REPORT_FILTER_DYNAMIC_DATE_OPTIONS,
} from "./reportDynamicDateOptions";

const filterOperators = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Not Contains" },
  { value: "greater_than", label: "Greater Than" },
  { value: "less_than", label: "Less Than" },
  { value: "between", label: "Between" },
  { value: "is_null", label: "Is Null" },
  { value: "is_not_null", label: "Is Not Null" },
];

const dateOperators = [
  { value: "greater_than", label: "Greater Than" },
  { value: "less_than", label: "Less Than" },
  { value: "greater_than_or_equal", label: "Greater Than or Equal" },
  { value: "less_than_or_equal", label: "Less Than or Equal" },
  { value: "between", label: "Between" },
  { value: "dynamic", label: "Dynamic" },
];

const enumOperators = [
  { value: "includes", label: "Includes" },
  { value: "not_includes", label: "Does Not Include" },
];

const formatTableName = (tableName) => {
  if (!tableName) return "";
  if (tableName === "deal_lead") return "Deal / Lead";
  if (tableName === "deal_worksheet") return "Deal Worksheet";
  return tableName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const formatEnumLabel = (option) => {
  if (!option) return "";
  if (typeof option === "object") {
    return option.label || option.name || "";
  }
  return option.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const enrichFilter = (existingFilter, index, transformedData, locationOptions, t) => {
  const fieldData = Object.values(transformedData)
    .flat()
    .find(
      (field) =>
        field.id === existingFilter.field_name ||
        field.id === existingFilter.id
    );

  const isSalesLocation =
    existingFilter.field_name === "sales_location_id" ||
    fieldData?.id === "sales_location_id";

  const enumValues = isSalesLocation
    ? locationOptions
    : fieldData?.enumValues ||
      existingFilter.enum_values ||
      existingFilter.options ||
      null;

  const operator =
    existingFilter.operator ||
    (enumValues ? "includes" : existingFilter.type === "date" ? "dynamic" : "equals");

  const rawValue =
    existingFilter.value ??
    (enumValues ? [] : existingFilter.type === "date" ? "this_month" : "");

  const value =
    existingFilter.type === "date"
      ? normalizeFilterValueForOperator(
          { type: "date", value: rawValue },
          operator
        )
      : rawValue;

  return {
    id: existingFilter.id || `schedule_filter_${index}`,
    field_name: existingFilter.field_name,
    operator,
    value,
    table_name: existingFilter.table_name,
    label:
      existingFilter.label ||
      fieldData?.label ||
      (isSalesLocation ? t("reports.schedule.salesLocationId") : existingFilter.field_name),
    type: fieldData?.type || existingFilter.type || "text",
    enumValues,
    is_active: existingFilter.is_active !== undefined ? existingFilter.is_active : true,
  };
};

const ReportScheduleFilterParameters = ({
  activeColor = "blue",
  isTheme2 = false,
  id,
  filters = [],
  onFiltersChange,
  colors: colorsProp,
  selectStyles: selectStylesProp,
  inputStyle: inputStyleProp,
}) => {
  const { t } = useTranslation();
  const { data: reportTableData, isLoading: isReportTableLoading } = useGetReportTable(id);
  const { data: locationsData, isLoading: isLocationsLoading } = useGetChannels("sales_location_branch");

  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const theme = useReportScheduleTheme(isTheme2, activeColorStyle);
  const colors = colorsProp || theme.colors;
  const selectStyles = selectStylesProp ?? theme.selectStyles;
  const inputStyle = inputStyleProp ?? theme.inputStyle;

  const locationOptions = useMemo(
    () =>
      (locationsData?.results || [])
        .filter((location) => location?.type === "sales_location_branch")
        .map((location) => location.name)
        .filter(Boolean),
    [locationsData]
  );

  const boxStyle = useMemo(
    () => getReportSchedulePanelBoxStyle(colors, { marginTop: 8 }),
    [colors]
  );

  const transformedData = useMemo(() => {
    if (!reportTableData?.data?.tables) return {};

    const tables = {};
    Object.keys(reportTableData.data.tables).forEach((tableName) => {
      tables[tableName] = reportTableData.data.tables[tableName].map((fieldObj) => {
        const fieldKey = Object.keys(fieldObj)[0];
        const fieldData = fieldObj[fieldKey];
        return {
          id: fieldKey,
          label:
            fieldData.label ||
            (fieldKey
              ? fieldKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
              : ""),
          type: fieldData.type || "text",
          tableName,
          enumValues: fieldData.options || fieldData.enum_values || null,
        };
      });
    });
    return tables;
  }, [reportTableData]);

  const scheduleFilters = useMemo(() => {
    if (!filters || filters.length === 0) {
      return [];
    }

    return filters
      .filter((filter) => filter.is_active !== false)
      .map((existingFilter, index) =>
        enrichFilter(existingFilter, index, transformedData, locationOptions, t)
      );
  }, [filters, transformedData, locationOptions, t]);

  const updateFilter = (filterId, field, value) => {
    const updatedFilters = filters.map((filter) =>
      filter.id === filterId ? { ...filter, [field]: value } : filter
    );
    onFiltersChange?.(updatedFilters);
  };

  const updateFilterOperator = (filter, nextOperator) => {
    const updatedFilters = filters.map((item) =>
      item.id === filter.id
        ? {
            ...item,
            operator: nextOperator,
            value: normalizeFilterValueForOperator(item, nextOperator),
          }
        : item
    );
    onFiltersChange?.(updatedFilters);
  };

  const getOperatorOptions = (filter) => {
    if (filter.enumValues) return enumOperators;
    if (filter.type === "date") return dateOperators;
    return filterOperators;
  };

  const hasValidationErrors = useMemo(
    () =>
      scheduleFilters.some(
        (filter) =>
          filter.is_active &&
          filter.enumValues &&
          (!Array.isArray(filter.value) || filter.value.length === 0)
      ),
    [scheduleFilters]
  );

  const getEnumBoxStyle = (hasError) => ({
    backgroundColor: colors.enumBg,
    border: `1px solid ${hasError ? colors.enumErrorBorder : colors.enumBorder}`,
    /* Enum checkboxları aşağı kaymasın diye üst/alt padding'i azaltıyoruz */
    padding: "6px 12px 6px 16px",
    maxHeight: 160,
    overflowY: "auto",
    overflowX: "hidden",
    color: colors.checkboxLabel,
  });

  const renderEnumValues = (filter) => {
    const hasError = !filter.value || filter.value.length === 0;

    if (!filter.enumValues || filter.enumValues.length === 0) {
      return (
        <div
          className="report-schedule-enum-box"
          style={{
            ...getEnumBoxStyle(false),
            maxHeight: "none",
            color: colors.mutedColor,
            fontSize: 13,
          }}
        >
          {t("reports.schedule.loadingFilterParameters")}
        </div>
      );
    }

    return (
      <div className="report-schedule-enum-box" style={getEnumBoxStyle(hasError)}>
        <div className="mb-2 d-flex align-items-center report-schedule-enum-check-row">
          <input
            type="checkbox"
            id={`selectAll_${filter.id}`}
            className="report-schedule-enum-checkbox"
            checked={
              Array.isArray(filter.value) &&
              filter.enumValues &&
              filter.value.length === filter.enumValues.length
            }
            onChange={(e) => {
              if (e.target.checked) {
                updateFilter(filter.id, "value", filter.enumValues);
              } else if (filter.value?.length > 1) {
                updateFilter(filter.id, "value", [filter.value[0]]);
              }
            }}
          />
          <label
            htmlFor={`selectAll_${filter.id}`}
            className="mb-0 report-schedule-enum-checkbox-label"
            style={{ fontStyle: "italic", color: colors.checkboxLabel }}
          >
            {t("reports.schedule.selectAll")}
          </label>
        </div>

        {filter.enumValues?.map((option, optionIndex) => (
          <div
            key={optionIndex}
            className="d-flex align-items-center mb-1 report-schedule-enum-check-row"
          >
            <input
              type="checkbox"
              id={`${filter.id}_${optionIndex}`}
              className="report-schedule-enum-checkbox"
              checked={Array.isArray(filter.value) && filter.value.includes(option)}
              onChange={(e) => {
                const currentValues = Array.isArray(filter.value) ? filter.value : [];

                if (e.target.checked) {
                  updateFilter(filter.id, "value", [...currentValues, option]);
                  return;
                }

                const newValues = currentValues.filter((v) => v !== option);
                if (newValues.length === 0) return;
                updateFilter(filter.id, "value", newValues);
              }}
            />
            <label
              htmlFor={`${filter.id}_${optionIndex}`}
              className="mb-0 report-schedule-enum-checkbox-label"
              style={{ color: colors.checkboxLabel }}
            >
              {formatEnumLabel(option)}
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderValueField = (filter) => {
    if (filter.enumValues) {
      return renderEnumValues(filter);
    }

    if (filter.type === "date" && filter.operator === "dynamic") {
      const dynamicValue = typeof filter.value === "string" ? filter.value : "";
      return (
        <Select
          activeColor={activeColor}
          styles={selectStyles}
          className="react-select"
          placeholder={t("reports.schedule.selectDynamicValue")}
          isSearchable={false}
          closeMenuOnSelect
          value={dynamicValue ? getDynamicDateOption(dynamicValue) : null}
          onChange={(option) => updateFilter(filter.id, "value", option?.value || "")}
          options={REPORT_FILTER_DYNAMIC_DATE_OPTIONS}
        />
      );
    }

    if (filter.type === "date" && filter.operator === "between") {
      return (
        <div className="d-flex align-items-center w-100" style={{ gap: 8 }}>
          <div className="react-datetime-container" style={{ flex: 1 }}>
            <ReactDatetime
              value={
                filter.value?.from ? moment(filter.value.from, "YYYY-MM-DD") : null
              }
              onChange={(date) => {
                const currentValue = filter.value || {};
                updateFilter(filter.id, "value", {
                  ...currentValue,
                  from:
                    date && moment.isMoment(date)
                      ? date.format("YYYY-MM-DD")
                      : date instanceof Date
                        ? moment(date).format("YYYY-MM-DD")
                        : "",
                });
              }}
              dateFormat="YYYY-MM-DD"
              timeFormat={false}
              inputProps={{
                placeholder: "From date...",
                className: "form-control",
                style: inputStyle,
              }}
            />
          </div>
          <span style={{ color: colors.bodyColor, fontWeight: 500 }}>to</span>
          <div className="react-datetime-container" style={{ flex: 1 }}>
            <ReactDatetime
              value={filter.value?.to ? moment(filter.value.to, "YYYY-MM-DD") : null}
              onChange={(date) => {
                const currentValue = filter.value || {};
                updateFilter(filter.id, "value", {
                  ...currentValue,
                  to:
                    date && moment.isMoment(date)
                      ? date.format("YYYY-MM-DD")
                      : date instanceof Date
                        ? moment(date).format("YYYY-MM-DD")
                        : "",
                });
              }}
              dateFormat="YYYY-MM-DD"
              timeFormat={false}
              inputProps={{
                placeholder: "To date...",
                className: "form-control",
                style: inputStyle,
              }}
            />
          </div>
        </div>
      );
    }

    if (filter.type === "date") {
      return (
        <ReactDatetime
          value={filter.value ? moment(filter.value, "YYYY-MM-DD") : null}
          onChange={(date) =>
            updateFilter(
              filter.id,
              "value",
              date && moment.isMoment(date)
                ? date.format("YYYY-MM-DD")
                : date instanceof Date
                  ? moment(date).format("YYYY-MM-DD")
                  : ""
            )
          }
          dateFormat="YYYY-MM-DD"
          timeFormat={false}
          inputProps={{
            placeholder: t("reports.schedule.selectDate"),
            className: "form-control",
            style: inputStyle,
          }}
        />
      );
    }

    return (
      <Input
        type={filter.type === "number" ? "number" : "text"}
        className="form-control"
        style={inputStyle}
        placeholder={t("reports.schedule.enterValue")}
        value={filter.value || ""}
        onChange={(e) => updateFilter(filter.id, "value", e.target.value)}
      />
    );
  };

  const activeFilters = scheduleFilters;
  const isLoadingFilters =
    (isReportTableLoading || isLocationsLoading) && activeFilters.length > 0;

  return (
    <div className="report-schedule-filter-parameters report-schedule-panel-box" style={boxStyle}>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h6
          className="mb-0 font-weight-bold report-schedule-panel-title"
          style={{ color: colors.titleColor }}
        >
          {t("reports.schedule.selectFilterParameters")}
        </h6>
        {hasValidationErrors && (
          <Alert
            className="mb-0 py-2 px-3 report-schedule-validation-alert"
            style={{
              fontSize: 13,
              maxWidth: "55%",
              background: activeColorStyle.background,
              backgroundImage: activeColorStyle.backgroundImage,
              borderColor: activeColorStyle.borderColor,
              color: "#ffffff",
            }}
          >
            {t("reports.validationError")}
          </Alert>
        )}
      </div>

      <Table responsive borderless style={{ marginBottom: 0, backgroundColor: "transparent" }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${colors.rowBorder}` }}>
            <th style={{ color: colors.headerColor, fontWeight: 400, fontSize: 13, width: "18%" }}>
              {t("reports.schedule.fieldName")}
            </th>
            <th style={{ color: colors.headerColor, fontWeight: 400, fontSize: 13, width: "18%" }}>
              {t("reports.schedule.operator")}
            </th>
            <th style={{ color: colors.headerColor, fontWeight: 400, fontSize: 13, width: "40%" }}>
              {t("reports.schedule.values")}
            </th>
            <th style={{ color: colors.headerColor, fontWeight: 400, fontSize: 13, width: "24%" }}>
              {t("reports.schedule.dataSourceName")}
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoadingFilters ? (
            <tr>
              <td colSpan={4} className="py-4 text-center" style={{ color: colors.mutedColor }}>
                {t("reports.schedule.loadingFilterParameters")}
              </td>
            </tr>
          ) : activeFilters.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-4 text-center" style={{ color: colors.mutedColor }}>
                {t("reports.schedule.noFilterParameters")}
              </td>
            </tr>
          ) : (
            activeFilters.map((filter) => (
              <tr key={filter.id} style={{ borderBottom: `1px solid ${colors.rowBorder}` }}>
                <td
                  className="report-schedule-filter-field-name"
                  style={{
                    verticalAlign: "top",
                    fontWeight: 700,
                    paddingTop: 16,
                    color: colors.bodyColor,
                  }}
                >
                  {filter.label}
                </td>
                <td style={{ verticalAlign: "top", paddingTop: 12, minWidth: 160 }}>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t("reports.schedule.selectOperator")}
                    isSearchable={false}
                    closeMenuOnSelect
                    value={getOperatorOptions(filter).find((op) => op.value === filter.operator)}
                    onChange={(option) =>
                      updateFilterOperator(
                        filter,
                        option?.value ||
                          (filter.enumValues
                            ? "includes"
                            : filter.type === "date"
                              ? "dynamic"
                              : "equals")
                      )
                    }
                    options={getOperatorOptions(filter)}
                  />
                </td>
                <td style={{ verticalAlign: "top", paddingTop: 12 }}>{renderValueField(filter)}</td>
                <td
                  className="report-schedule-filter-datasource"
                  style={{
                    verticalAlign: "top",
                    fontWeight: 700,
                    paddingTop: 16,
                    color: colors.bodyColor,
                  }}
                >
                  {formatTableName(filter.table_name)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ReportScheduleFilterParameters;
